import React from 'react';

function GuidePage({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 섹션 */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4 animate-bounce">📖</div>
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            이용 가이드
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            효과적인 면접 연습을 위한 완벽 가이드
          </p>
        </div>

        {/* 빠른 시작 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🚀 빠른 시작 가이드
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center text-3xl font-bold">1</div>
              <h3 className="font-bold mb-2 text-lg">🎯 모드 선택</h3>
              <p className="text-sm leading-relaxed">기본, 심화, 직무, 상황 질문 중 하나를 선택하세요</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-3xl font-bold">2</div>
              <h3 className="font-bold mb-2 text-lg">🎤 환경 설정</h3>
              <p className="text-sm leading-relaxed">마이크 권한을 허용하고 조용한 곳에서 시작하세요</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-full flex items-center justify-center text-3xl font-bold">3</div>
              <h3 className="font-bold mb-2 text-lg">💬 답변 연습</h3>
              <p className="text-sm leading-relaxed">질문을 듣고 45초 안에 간결하게 답변하세요</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full flex items-center justify-center text-3xl font-bold">4</div>
              <h3 className="font-bold mb-2 text-lg">🔄 반복 연습</h3>
              <p className="text-sm leading-relaxed">다양한 유형으로 반복 연습하여 실력을 향상시키세요</p>
            </div>
          </div>
        </div>

        {/* 면접 유형별 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            📚 면접 유형별 가이드
          </h2>
          
          <div className="space-y-6">
            {/* 기본질문 가이드 */}
            <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-3">🔰</span>
                기본질문 가이드
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">📝 주요 질문 유형:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 자기소개</li>
                    <li>• 지원동기</li>
                    <li>• 장단점</li>
                    <li>• 입사 후 포부</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">💡 답변 팁:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 간결하고 명확하게</li>
                    <li>• 구체적인 경험 포함</li>
                    <li>• 회사와 연결지어 설명</li>
                    <li>• 긍정적인 표현 사용</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 심화질문 가이드 */}
            <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-3">🎯</span>
                심화질문 가이드
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🔍 주요 질문 유형:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 가치관 관련</li>
                    <li>• 인생 경험</li>
                    <li>• 성격 및 성향</li>
                    <li>• 미래 계획</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">🎭 답변 팁:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 진정성 있게 답변</li>
                    <li>• 실제 경험담 활용</li>
                    <li>• 성장 과정 강조</li>
                    <li>• 회사 가치와 연결</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 직무질문 가이드 */}
            <div className={`p-6 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-3">💼</span>
                직무질문 가이드
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🏢 주요 질문 유형:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 직무 이해도</li>
                    <li>• 전문 기술/지식</li>
                    <li>• 프로젝트 경험</li>
                    <li>• 업계 트렌드</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">⚡ 답변 팁:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 전문 용어 적절히 사용</li>
                    <li>• 구체적인 사례 제시</li>
                    <li>• 학습 의지 표현</li>
                    <li>• 실무 연결성 강조</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 상황질문 가이드 */}
            <div className={`p-6 rounded-xl border-l-4 border-orange-500 ${darkMode ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-3">🧩</span>
                상황질문 가이드
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🎬 주요 질문 유형:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 갈등 해결</li>
                    <li>• 문제 해결</li>
                    <li>• 리더십 발휘</li>
                    <li>• 윤리적 판단</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">🔧 답변 팁:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• STAR 방법 활용</li>
                    <li>• 논리적 사고 과정</li>
                    <li>• 다양한 관점 고려</li>
                    <li>• 윤리적 판단 우선</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 효과적인 답변 요령 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            💡 효과적인 답변 요령
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* STAR 방법 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-indigo-900 bg-opacity-30' : 'bg-indigo-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-center">
                ⭐ STAR 방법
              </h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="font-bold text-blue-600 mr-2">S</span>
                  <div>
                    <span className="font-semibold">Situation (상황)</span>
                    <p className="text-sm">구체적인 상황을 설명</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">T</span>
                  <div>
                    <span className="font-semibold">Task (과제)</span>
                    <p className="text-sm">해결해야 할 문제나 목표</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="font-bold text-green-600 mr-2">A</span>
                  <div>
                    <span className="font-semibold">Action (행동)</span>
                    <p className="text-sm">취한 구체적인 행동</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="font-bold text-orange-600 mr-2">R</span>
                  <div>
                    <span className="font-semibold">Result (결과)</span>
                    <p className="text-sm">얻은 성과와 배운 점</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 시간 관리 팁 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-pink-900 bg-opacity-30' : 'bg-pink-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-center">
                ⏰ 시간 관리 팁
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-blue-600">0-10초:</span>
                  <p className="text-sm">질문 이해 및 답변 구조 설계</p>
                </div>
                <div>
                  <span className="font-semibold text-purple-600">10-35초:</span>
                  <p className="text-sm">핵심 내용 전달 (경험, 사례)</p>
                </div>
                <div>
                  <span className="font-semibold text-green-600">35-40초:</span>
                  <p className="text-sm">결론 및 회사와의 연관성</p>
                </div>
                <div>
                  <span className="font-semibold text-orange-600">40-45초:</span>
                  <p className="text-sm">마무리 및 여유시간</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 기술적 요구사항 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🔧 기술적 요구사항
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* 브라우저 지원 */}
            <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <div className="text-4xl mb-4">🌐</div>
              <h3 className="font-bold mb-3">지원 브라우저</h3>
              <ul className="text-sm space-y-1">
                <li>✅ Chrome (권장)</li>
                <li>✅ Safari</li>
                <li>✅ Edge</li>
                <li>⚠️ Firefox (제한적)</li>
              </ul>
            </div>

            {/* 기기 지원 */}
            <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <div className="text-4xl mb-4">📱</div>
              <h3 className="font-bold mb-3">지원 기기</h3>
              <ul className="text-sm space-y-1">
                <li>✅ 데스크탑/노트북</li>
                <li>✅ 태블릿</li>
                <li>✅ 스마트폰</li>
                <li>✅ 마이크 필수</li>
              </ul>
            </div>

            {/* 네트워크 요구사항 */}
            <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <div className="text-4xl mb-4">📡</div>
              <h3 className="font-bold mb-3">네트워크 요구사항</h3>
              <ul className="text-sm space-y-1">
                <li>✅ 안정적인 인터넷 연결</li>
                <li>✅ 최소 1Mbps 속도</li>
                <li>✅ AWS 접근 가능</li>
                <li>⚠️ VPN 사용 시 주의</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 문제 해결 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🛠️ 문제 해결 가이드
          </h2>
          
          <div className="space-y-6">
            {/* 음성 인식 문제 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'} border-l-4 border-red-500`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🎤</span>
                음성 인식이 안 될 때
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🔍 원인:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 마이크 권한 미허용</li>
                    <li>• 브라우저 호환성 문제</li>
                    <li>• 네트워크 연결 불안정</li>
                    <li>• 주변 소음이 너무 큼</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">💡 해결방법:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 브라우저 새로고침</li>
                    <li>• 마이크 권한 다시 허용</li>
                    <li>• Chrome 브라우저 사용</li>
                    <li>• 조용한 환경에서 시도</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 음성 출력 문제 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'} border-l-4 border-orange-500`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🔊</span>
                질문 음성이 안 들릴 때
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🔍 원인:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 스피커 볼륨 설정</li>
                    <li>• AWS Polly 연결 문제</li>
                    <li>• 브라우저 오디오 차단</li>
                    <li>• 네트워크 지연</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">💡 해결방법:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 볼륨 확인 및 조정</li>
                    <li>• 브라우저 새로고침</li>
                    <li>• 오디오 자동재생 허용</li>
                    <li>• 잠시 후 다시 시도</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 페이지 로딩 문제 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">⏳</span>
                페이지가 느리게 로딩될 때
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">🔍 원인:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 네트워크 속도 저하</li>
                    <li>• 브라우저 캐시 문제</li>
                    <li>• 서버 일시적 지연</li>
                    <li>• 기기 성능 문제</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">💡 해결방법:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 브라우저 캐시 삭제</li>
                    <li>• 다른 탭 닫기</li>
                    <li>• 네트워크 연결 확인</li>
                    <li>• 잠시 후 재시도</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 면접 준비 체크리스트 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            ✅ 면접 준비 체크리스트
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* 기술적 준비 */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-2">🔧</span>
                기술적 준비
              </h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">마이크 작동 확인</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">스피커/이어폰 음량 조정</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">안정적인 인터넷 연결</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">조용한 환경 조성</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">휴대폰 녹음 앱 준비</span>
                </label>
              </div>
            </div>

            {/* 내용적 준비 */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-2">📝</span>
                내용적 준비
              </h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">자기소개 핵심 포인트 정리</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">지원 회사 정보 숙지</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">직무 관련 경험 정리</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">STAR 방법 숙지</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">예상 질문 리스트 작성</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            ❓ 자주 묻는 질문 (FAQ)
          </h2>
          
          <div className="space-y-4">
            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 회원가입이 필요한가요?</summary>
              <p className="mt-2 text-sm">아니요! 별도의 회원가입 없이 바로 이용하실 수 있습니다.</p>
            </details>

            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 내 답변이 저장되나요?</summary>
              <p className="mt-2 text-sm">음성 데이터는 저장되지 않습니다. 개인 휴대폰으로 녹음하시면 복습이 가능합니다.</p>
            </details>

            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 모바일에서도 사용할 수 있나요?</summary>
              <p className="mt-2 text-sm">네! PC, 태블릿, 스마트폰 모두에서 사용 가능합니다.</p>
            </details>

            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 질문을 건너뛸 수 있나요?</summary>
              <p className="mt-2 text-sm">'다음 질문' 버튼을 클릭하면 언제든 다음 질문으로 넘어갈 수 있습니다.</p>
            </details>

            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 시간을 더 늘릴 수 있나요?</summary>
              <p className="mt-2 text-sm">'+15초', '+30초' 버튼으로 답변 시간을 연장할 수 있습니다.</p>
            </details>

            <details className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <summary className="font-semibold cursor-pointer">Q. 몇 번까지 연습할 수 있나요?</summary>
              <p className="mt-2 text-sm">횟수 제한 없이 무제한으로 연습하실 수 있습니다!</p>
            </details>
          </div>
        </div>

        {/* 연락처 및 지원 */}
        <div className="text-center">
          <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12`}>
            <div className="text-6xl mb-6">💌</div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              추가 도움이 필요하신가요?
            </h2>
            <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              언제든지 문의해주세요! 최대한 빠르게 도와드리겠습니다.
            </p>
            <div className="space-y-2">
              <p className="text-sm">
                📧 이메일: job1004jane@gmail.com
              </p>
              <p className="text-sm">
                💬 문의사항이나 개선 제안은 언제나 환영합니다!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuidePage;